import React, { useState, useEffect, useRef } from 'react';
import { 
  LuArrowLeft, LuBox, LuMail, LuSend, 
  LuAlertCircle, LuCheckCircle, LuLoader2, LuLightbulb, LuLogIn 
} from "react-icons/lu";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import { AlertCircle, CheckCircle, Loader2, Loader2Icon } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // Ensure you have react-router-dom installed

import "../../asset/css/reset-password.css";
import logo from "../../asset/img/white.png";

const ResetPassword = () => {
  // State Management
  const [email, setEmail] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '', show: false });
  
  const navigate = useNavigate();
  const emailInputRef = useRef(null);

  // Auto-focus on mount
  useEffect(() => {
    if (emailInputRef.current) emailInputRef.current.focus();
  }, []);

  // --- AUTOMATIC NAVIGATION LOGIC ---
  // When step 3 is reached, wait 2 seconds then go to login
  useEffect(() => {
    if (currentStep === 3) {
      const timer = setTimeout(() => {
        window.location.href = '/login'; // Or navigate('/login')
      }, 100); // 2.5 seconds to give user time to read the success msg
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Message Helper
  const displayMsg = (type, text) => {
    setMsg({ type, text, show: true });
    setTimeout(() => setMsg(prev => ({ ...prev, show: false })), 5000);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // --- HANDLES API CALL ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      displayMsg('error', 'Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      displayMsg('error', 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setMsg({ ...msg, show: false });

    try {
      const response = await axios.post(`${baseurl}forgotPassword`, { 
        email: email 
      });

      if (response.data.success) {
        setCurrentStep(2);
        displayMsg('success', 'Email found! Preparing your link...');

        // Transition to Success Screen
        setTimeout(() => {
          setCurrentStep(3);
          setIsSubmitted(true);
          setIsLoading(false);
        }, 1500); 
      } else {
        setIsLoading(false);
        displayMsg('error', response.data.message || 'Account not found.');
      }
    } catch (error) {
      setIsLoading(false);
      const errorText = error.response?.data?.message || 'Server connection failed.';
      displayMsg('error', errorText);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-around align-items-center sticky-top bg-light py-2">
        <a href="/" className="logo">
          <div>
            <img
              src={logo}
              className="img-fluid"
              style={{ maxHeight: "40px" }}
            />
            {/* <div className="sub-brand  text-dark">Project Management Dashboard</div> */}
          </div>
        </a>

        <a href="/login" className="back-link">
          <LuArrowLeft /> Back to Login
        </a>
      </div>

      <div className="container">
        {/* Header */}
        <div className="header">
          <h1 className="title">Reset Your Password</h1>
          <p className="subtitle">
            Enter your email address and we'll send you a password reset link
          </p>
        </div>

        {/* Steps Indicator */}
        <div className="steps">
          <div className={`step ${currentStep >= 1 ? "active" : ""}`}></div>
          <div className={`step ${currentStep >= 2 ? "active" : ""}`}></div>
          <div className={`step ${currentStep >= 3 ? "active" : ""}`}></div>
        </div>

        {/* Global Alerts */}
        <div
          className={`message error ${msg.type === "error" && msg.show ? "show" : ""}`}
        >
          <AlertCircle size={18} />
          <span>{msg.text}</span>
        </div>

        <div
          className={`message success ${msg.type === "success" && msg.show ? "show" : ""}`}
        >
          <CheckCircle size={18} />
          <span>{msg.text}</span>
        </div>

        {/* Main Card Content */}
        <div
          className="reset-card"
          style={{
            minHeight: "380px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {!isSubmitted ? (
            /* FORM VIEW (Step 1 & 2) */
            <div id="resetForm" style={{ width: "100%" }}>
              <form id="passwordResetForm" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <div className="input-with-icon">
                    <LuMail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      className="form-input-reset"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      ref={emailInputRef}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary" 
                  disabled={isLoading}
                  style={{ width: "100%",  background: "#4318ff" }}
                >
                  {isLoading ? (
                    <>
                      <Loader2Icon className="spinner" /> Sending...
                    </>
                  ) : (
                    <>
                      <LuSend /> Send Reset Link
                    </>
                  )}
                </button>
              </form>

              <div className="instructions">
                <p>
                  <strong>What happens next:</strong>
                </p>
                <ul>
                  <li>You'll receive an email with a reset link</li>
                  <li>Click the link to create a new password</li>
                </ul>
              </div>
            </div>
          ) : (
            /* CORRECTED SUCCESS VIEW (Step 3) */
            <div
              className="success-screen"
              style={{ textAlign: "center", padding: "20px" }}
            >
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    background: "#ecfdf5",
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  <CheckCircle size={48} color="#10b981" />
                </div>
              </div>

              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#111827",
                  marginBottom: "10px",
                }}
              >
                Email Sent!
              </h2>

              <p
                style={{
                  color: "#6B7280",
                  fontSize: "15px",
                  marginBottom: "20px",
                }}
              >
                We've sent a link to <br /> <strong>{email}</strong>
              </p>

              <div
                style={{
                  background: "#f0fdf4",
                  padding: "12px",
                  borderRadius: "8px",
                  color: "#166534",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Redirecting to login in 2 seconds...
              </div>

              <button
                className="btn btn-primary"
                style={{
                  width: "100%",
                  marginTop: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                onClick={() => (window.location.href = "/login")}
              >
                <LuLogIn /> Go Now
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="footer">
          <div className="footer-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Support</a>
          </div>
          <p>© 2026 Measro LLC. All rights reserved.</p>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;